import React, { useState, useEffect } from "react";
import {
  FaHashtag,
  FaTrash,
  FaTimes,
  FaUndoAlt,
  FaExclamationTriangle,
  FaSpinner,
  FaCheckCircle,
  FaSave,
  FaInfoCircle,
} from "react-icons/fa";

import apiRequest from "../services/api";

const ReturnCreate = ({ isOpen, invoice, onClose, onSaveSuccess }) => {
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [returnItems, setReturnItems] = useState([]);
  const [itemQuantities, setItemQuantities] = useState({});
  const [linkInvoice, setLinkInvoice] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [note, setNote] = useState("");
  const [docType, setDocType] = useState("CRE"); // CRE = Credit Note, DBE = Debit Note

  const isSaleContext =
    invoice?.type === "SALE" ||
    invoice?.type === "SALE_RETURN" ||
    invoice?.parentInvoiceType === "SALE";

  const isReturnInvoice = ["SALE_RETURN", "PURCHASE_RETURN"].includes(
    invoice?.type
  );

  const currentStatus = invoice?.status?.toUpperCase();

  // True if viewing/editing an existing return draft/invoice
  const isExistingRecord = !!invoice?.id && isReturnInvoice;
  const isDraft = currentStatus === "DRAFT" || currentStatus === "CONFIRMED";

  const [selectedProcessAction, setSelectedProcessAction] = useState(null);

  const handleModalClose = () => {
    setError("");
    setSelectedProcessAction(null);
    setInvoiceNumber("");
    setLinkInvoice(false);
    setDocType("CRE");
    setNote("");
    onClose();
  };

  useEffect(() => {
    const hydrateReturnWorkspace = async () => {
      if (!isOpen || !invoice?.id) return;

      setError("");
      setIsDataLoading(true);
      setSelectedProcessAction(null);
      let runningItemsList = invoice.items || [];

      try {
        const sourceId = invoice.id || invoice.parentInvoiceId;
        const detailResponse = await apiRequest(`invoices/${sourceId}`);
        const resolvedResponsePayload = detailResponse?.data;
        const fetchedItems = resolvedResponsePayload?.items;

        if (Array.isArray(fetchedItems)) {
          runningItemsList = fetchedItems;
        } else if (
          resolvedResponsePayload &&
          typeof resolvedResponsePayload === "object" &&
          !fetchedItems
        ) {
          runningItemsList =
            resolvedResponsePayload.items || invoice.items || [];
        }
      } catch (err) {
        console.error("Return layout synchronization error:", err);
        setError(
          "Failed to coordinate backend sync. Using fallback local arrays."
        );
      }

      setReturnItems(runningItemsList);

      const initialMap = {};
      runningItemsList.forEach((item) => {
        initialMap[item.id] = "";
      });
      setItemQuantities(initialMap);
      setIsDataLoading(false);
    };

    hydrateReturnWorkspace();
  }, [isOpen, invoice?.id]);

  if (!isOpen || !invoice) return null;

  // Credit notes are limited to processed quantity less previously returned quantity.
  // Debit notes represent additional quantity, so they are not capped by the original processed quantity.
  const getMaxReturnableQty = (item) => {
    if (docType === "DBE") return Infinity;
    const rawProcessed = isSaleContext
      ? item.deliveredQuantity
      : item.receivedQuantity;

    // Fall back to original ordered quantity if fulfillment tracking isn't populated
    const processedQty =
      rawProcessed !== undefined && rawProcessed !== null
        ? Number(rawProcessed)
        : Number(item.quantity || 0);

    const alreadyReturned = Number(item.returnedQuantity || 0);
    return Math.max(0, processedQty - alreadyReturned);
  };

  const handleQtyChange = (itemId, val, maxAvailable) => {
    const parsedVal = val === "" ? "" : Math.max(0, parseFloat(val) || 0);
    if (
      maxAvailable !== Infinity &&
      parsedVal !== "" &&
      parsedVal > maxAvailable
    ) {
      setItemQuantities((prev) => ({ ...prev, [itemId]: maxAvailable }));
      return;
    }
    setItemQuantities((prev) => ({ ...prev, [itemId]: parsedVal }));
  };

  const handleExecuteWorkflowAction = async (actionType) => {
    setError("");
    setIsLoading(true);
    setSelectedProcessAction(actionType);

    try {
      if (actionType === "RETURN") {
        await apiRequest(`invoices/${invoice.id}/return`, {
          method: "POST",
        });
      } else {
        await apiRequest(`invoices/${invoice.id}/delete?action=${actionType}`, {
          method: "PUT",
        });
      }

      onSaveSuccess?.();
      handleModalClose();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          `Failed to execute ${actionType}`
      );
    } finally {
      setIsLoading(false);
      setSelectedProcessAction(null);
    }
  };

  const handleFormDispatch = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // 1. Build the array of line items
    const payloadItems = returnItems
      .map((item) => {
        const targetProductId = item.product?.id || item.productId;

        const payloadRow = {
          product: targetProductId ? { id: parseInt(targetProductId) } : null,
          quantity: parseFloat(itemQuantities[item.id]) || 0,
        };

        return payloadRow;
      })
      .filter((item) => item.quantity > 0 && item.product !== null);

    if (payloadItems.length === 0) {
      setError(
        `${
          docType === "CRE" ? "Credit" : "Debit"
        } note rejected: Enter a quantity greater than 0 for at least one item.`
      );
      setIsLoading(false);
      return;
    }

    // 2. Wrap everything into a single Invoice object payload matching Spring Boot model
    const invoicePayload = {
      invoiceNumber,
      docType, // CRE = Credit Note, DBE = Debit Note
      parentInvoice: { id: invoice.id },
      items: payloadItems,
      note: note,
    };

    try {
      // 3. Send the single object to backend via POST
      const createdReturn = await apiRequest(
        `invoices/${invoice.id}/create-return`,
        {
          method: "POST",
          body: invoicePayload,
        }
      );

      onSaveSuccess?.(createdReturn);
      handleModalClose();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          `Failed to create ${docType === "CRE" ? "credit" : "debit"} note.`
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4">
      <div className="relative w-full max-w-5xl bg-slate-900/75 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl text-slate-100 max-h-[90vh] flex flex-col gap-4">
        {/* Header Section */}
        <div className="flex justify-between items-center pb-3 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/5 border border-white/10 rounded-xl">
              <FaUndoAlt className="text-amber-400 text-lg" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white tracking-wide">
                {isExistingRecord ? "Credit/Debit Note Details" : "Create Note"}
              </h3>
              <p className="text-[11px] text-slate-400 mt-0.5 font-mono">
                Source Document:{" "}
                {invoice.parentInvoiceNumber || invoice.invoiceNumber} •{" "}
                <span className="text-amber-400 uppercase font-bold">
                  {invoice?.type}
                </span>
              </p>

              {/* Workflow action buttons — Strictly only if existing record AND status is DRAFT or CONFIRMED */}
              {isExistingRecord && isDraft && (
                <div className="flex items-center bg-slate-950/40 border border-white/5 rounded-xl p-1 gap-1.5 w-fit mt-2">
                  {[
                    {
                      action: "DELETE",
                      label: "Delete",
                      icon: <FaTrash />,
                      style:
                        "bg-red-600/10 hover:bg-red-600 border-red-500/20 text-red-400 hover:text-white",
                    },
                  ].map(({ action, label, icon, style }) => (
                    <button
                      key={action}
                      type="button"
                      disabled={isLoading}
                      onClick={() => {
                        const messages = {
                          DELETE:
                            "Delete this return draft? This cannot be undone.",
                          CONFIRM:
                            "Confirm this return draft? Stock and accounting entries will be posted.",
                        };

                        if (
                          messages[action] &&
                          !window.confirm(messages[action])
                        ) {
                          return;
                        }

                        handleExecuteWorkflowAction(action);
                      }}
                      className={`px-3 py-1.5 rounded-lg border transition flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${style}`}
                    >
                      {isLoading && selectedProcessAction === action ? (
                        <FaSpinner className="animate-spin" />
                      ) : (
                        icon
                      )}
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            disabled={isLoading || isDataLoading}
            onClick={handleModalClose}
            className="p-1.5 rounded-lg bg-white/5 text-slate-400 hover:text-white transition"
          >
            <FaTimes className="text-xs" />
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs flex items-center gap-2">
            <FaExclamationTriangle /> {error}
          </div>
        )}

        {isDataLoading ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 p-12 text-slate-400 text-xs">
            <FaSpinner className="animate-spin text-2xl text-amber-500" />
            <span>Querying ...</span>
          </div>
        ) : (
          <form
            onSubmit={handleFormDispatch}
            className="flex-1 overflow-y-auto flex flex-col gap-4 text-xs"
          >
            {!isExistingRecord && (
              <>
                {/* Select whether this document is a credit note or debit note. */}
                <div className="flex flex-wrap items-center gap-4 rounded-xl border border-white/5 bg-slate-950/30 px-4 py-3">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
                    Document Type 
                  </span>
                  {[
                    {
                      value: "CRE",
                      label: "Credit Note (CRE)",
                      description: "Reduce quantity / value",
                    },
                    {
                      value: "DBE",
                      label: "Debit Note (DBE)",
                      description: "Add additional quantity / value",
                    },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 transition ${
                        docType === option.value
                          ? "border-emerald-500/50 bg-emerald-500/10"
                          : "border-white/10 bg-slate-900/40"
                      }`}
                    >
                      <input
                        type="radio"
                        name="docType"
                        value={option.value}
                        checked={docType === option.value}
                        onChange={() => {
                          setDocType(option.value);
                          setItemQuantities({});
                        }}
                        className="mt-0.5 accent-emerald-500"
                      />
                      <span>
                        <span className="block text-[11px] font-bold text-white">
                          {option.label}
                        </span>
                        <span className="mt-0.5 block text-[10px] text-slate-400">
                          {option.description}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                <p className="-mt-2 text-[10px] text-slate-400">
                  {docType === "CRE"
                    ? "Credit note: quantity cannot exceed the processed quantity that has not already been returned."
                    : "Debit note: quantity entered is additional to the original invoice quantity."}
                </p>
                <div className="flex items-center gap-3 w-full">
                  {/* Toggle Checkbox */}
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    Description
                  </label>

                  <div className="relative flex-1">
                    <input
                      type="text"
                      required={true}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Description"
                      className={`w-full bg-slate-950 border rounded-xl pl-9 pr-3 py-2.5 text-xs text-white placeholder-slate-600 focus:outline-none transition-all ${"border-white/10 focus:border-emerald-500/50"}`}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="border border-white/5 rounded-xl overflow-hidden bg-slate-950/20">
              <table className="w-full text-left border-collapse min-w-[750px]">
                <thead>
                  <tr className="border-b border-white/5 bg-slate-950/40 text-slate-400 text-[10px] font-bold tracking-wider uppercase">
                    <th className="px-4 py-3 w-4/12">Product Description</th>
                    <th className="px-3 py-3 text-center w-24">Processed</th>
                    <th className="px-3 py-3 text-center w-28 text-amber-400">
                      Prev. Returned
                    </th>
                    <th className="px-3 py-3 text-center w-28 text-emerald-400">
                      {docType === "CRE" ? "Max Returnable" : "Additional Qty"}
                    </th>
                    <th className="px-4 py-3 text-right w-36 bg-white/[0.02]">
                      QTY *
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-white">
                  {returnItems.map((item) => {
                    const rawProcessed = isSaleContext
                      ? item.deliveredQuantity
                      : item.receivedQuantity;

                    const processedQty =
                      rawProcessed !== undefined && rawProcessed !== null
                        ? Number(rawProcessed)
                        : Number(item.quantity || 0);

                    const alreadyReturned = Number(item.returnedQuantity || 0);
                    const maxAllowedReturn = getMaxReturnableQty(item);
                    const isRowFullySettled =
                      docType === "CRE" && maxAllowedReturn <= 0;

                    const batchDisplay =
                      typeof item.stockBatch === "object"
                        ? item.stockBatch?.batchNumber
                        : item.batchNumber || item.stockBatch || "N/A";

                    return (
                      <tr
                        key={item.id}
                        className={`hover:bg-white/[0.01] ${
                          isRowFullySettled ? "opacity-40 bg-slate-950/20" : ""
                        }`}
                      >
                        <td className="px-4 py-3 font-sans">
                          <p className="text-white font-semibold text-xs flex items-center gap-2">
                            {item.product?.name ||
                              item.productName ||
                              "Unknown Catalog Reference"}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            Batch: {batchDisplay}{" "}
                            {item.expiryDate ? `| Exp: ${item.expiryDate}` : ""}
                          </p>
                        </td>

                        {/* Processed Qty (Received / Delivered) */}
                        <td className="px-3 py-3 text-center font-mono font-semibold text-slate-300">
                          {processedQty.toFixed(2)}
                        </td>

                        {/* Already Returned Qty */}
                        <td className="px-3 py-3 text-center font-mono text-amber-400/90 font-medium">
                          {alreadyReturned > 0 ? (
                            <span className="px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20">
                              {alreadyReturned.toFixed(2)}
                            </span>
                          ) : (
                            "0.00"
                          )}
                        </td>

                        {/* Credit notes are capped by available return quantity; debit notes add quantity. */}
                        <td className="px-3 py-3 text-center font-mono font-bold text-emerald-400">
                          {docType === "CRE"
                            ? maxAllowedReturn.toFixed(2)
                            : "No limit"}
                        </td>

                        {/* Credit Note Return / Debit Note Additional Quantity Input */}
                        <td className="px-4 py-2 bg-white/[0.01] text-right">
                          {isRowFullySettled ? (
                            <div className="text-emerald-400 text-xs font-sans font-semibold flex items-center justify-end gap-1 px-2 py-1">
                              <FaCheckCircle className="text-xs" /> Fully
                              Returned
                            </div>
                          ) : (
                            <input
                              type="number"
                              step="any"
                              min="0"
                              disabled={isLoading || isExistingRecord}
                              placeholder={
                                docType === "CRE"
                                  ? `Max ${maxAllowedReturn}`
                                  : "Additional qty"
                              }
                              value={itemQuantities[item.id] ?? ""}
                              onChange={(e) =>
                                handleQtyChange(
                                  item.id,
                                  e.target.value,
                                  maxAllowedReturn
                                )
                              }
                              className="w-28 bg-slate-950 border border-white/10 rounded-lg px-2.5 py-1.5 text-right text-white font-bold focus:border-emerald-500/50 disabled:opacity-50"
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Bottom Action Footer */}
            <div className="flex justify-between items-center pt-3 border-t border-white/5 mt-auto font-sans">
              <div className="flex gap-3">
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl bg-white/5 text-slate-300 font-semibold transition hover:bg-white/10"
                >
                  Cancel
                </button>

                {/* Rendered ONLY during CREATION mode */}
                {!isExistingRecord && (
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-5 py-2.5 rounded-xl text-xs flex items-center gap-2 shadow-lg bg-amber-600 hover:bg-amber-500 text-slate-950 font-bold transition"
                  >
                    {isLoading ? (
                      <>
                        <FaSpinner className="animate-spin" /> Saving Draft...
                      </>
                    ) : (
                      <>
                        <FaSave /> Save as Draft
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReturnCreate;
